import { MESSAGE_TYPE } from 'shared/constants/messages';
import { showBadgeOnFavicon } from './faviconHelper';
import { initFaviconSwitcher } from './faviconHelper';

import { EVENT_TYPES } from 'dashboard/routes/dashboard/settings/profile/constants.js';
import GlobalStore from 'dashboard/store';
import AudioNotificationStore from './AudioNotificationStore';
import {
  isConversationAssignedToMe,
  isConversationUnassigned,
  isMessageFromCurrentUser,
} from './AudioMessageHelper';
import WindowVisibilityHelper from './WindowVisibilityHelper';
import { useAlert } from 'dashboard/composables';
import {
  DEFAULT_NOTIFICATION_SOUND_ENABLED,
  DEFAULT_NOTIFICATION_SOUND_PREFERENCES,
  DEFAULT_REPEAT_NEW_MESSAGE_SOUND_UNTIL_READ,
  LEGACY_TONE_FILE_MAP,
  NOTIFICATION_SOUND_EVENTS,
  NOTIFICATION_SOUND_FILE_MAP,
  sanitizeNotificationSoundPreferences,
} from './constants';

const NOTIFICATION_TIME = 30000;
const ALERT_DURATION = 10000;
const ALERT_PATH_PREFIX = '/audio/dashboard/';
const DEFAULT_TONE = 'message';
const DEFAULT_ALERT_TYPE = ['none'];
const DEFAULT_ALERT_TYPE_STRING = DEFAULT_ALERT_TYPE.join('+');
const NOTIFICATION_TYPE_TO_SOUND_EVENT = Object.freeze({
  conversation_assignment: NOTIFICATION_SOUND_EVENTS.NEW_TICKETS_TO_HANDLE,
});

export class DashboardAudioNotificationHelper {
  constructor(store) {
    if (!store) {
      throw new Error('store is required');
    }
    this.store = new AudioNotificationStore(store);

    this.notificationConfig = {
      audioAlertType: DEFAULT_ALERT_TYPE,
      playAlertOnlyWhenHidden: true,
      alertIfUnreadConversationExist: false,
    };

    this.notificationSoundConfig = {
      enabled: DEFAULT_NOTIFICATION_SOUND_ENABLED,
      repeatNewMessageSoundUntilRead:
        DEFAULT_REPEAT_NEW_MESSAGE_SOUND_UNTIL_READ,
      preferences: { ...DEFAULT_NOTIFICATION_SOUND_PREFERENCES },
    };

    this.recurringNotificationTimer = null;

    this.audioConfig = {
      audio: null,
      tone: DEFAULT_TONE,
      hasSentSoundPermissionsRequest: false,
      eventAudios: {},
    };

    this.currentUser = null;
  }

  buildAudioResourceUrl = soundFileName => {
    return `${ALERT_PATH_PREFIX}${soundFileName}.mp3`;
  };

  resolveLegacyTone = tone => {
    return LEGACY_TONE_FILE_MAP[tone] || DEFAULT_TONE;
  };

  intializeAudio = () => {
    const toneFile = this.resolveLegacyTone(this.audioConfig.tone);
    const resourceUrl = this.buildAudioResourceUrl(toneFile);
    this.audioConfig.audio = new Audio(resourceUrl);
    return this.audioConfig.audio.load();
  };

  playAudioElement = async audio => {
    try {
      await audio.play();
    } catch (error) {
      if (
        error.name === 'NotAllowedError' &&
        !this.audioConfig.hasSentSoundPermissionsRequest
      ) {
        this.audioConfig.hasSentSoundPermissionsRequest = true;
        useAlert(
          'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.SOUND_PERMISSION_ERROR',
          { usei18n: true, duration: ALERT_DURATION }
        );
      }
    }
  };

  triggerAudioPlayback = audio => {
    if (!audio) {
      return false;
    }

    audio.currentTime = 0;
    this.playAudioElement(audio);
    return true;
  };

  getEventAudio = eventKey => {
    const soundFileName = NOTIFICATION_SOUND_FILE_MAP[eventKey];
    if (!soundFileName) {
      return null;
    }

    if (!this.audioConfig.eventAudios[soundFileName]) {
      const resourceUrl = this.buildAudioResourceUrl(soundFileName);
      this.audioConfig.eventAudios[soundFileName] = new Audio(resourceUrl);
    }

    return this.audioConfig.eventAudios[soundFileName];
  };

  playAudioAlert = () => {
    if (!this.audioConfig.audio) {
      this.intializeAudio();
    }
    return this.triggerAudioPlayback(this.audioConfig.audio);
  };

  isNotificationSoundEnabled = eventKey => {
    return (
      this.notificationSoundConfig.enabled &&
      !!this.notificationSoundConfig.preferences[eventKey]
    );
  };

  playNotificationSound = eventKey => {
    if (!this.isNotificationSoundEnabled(eventKey)) {
      return false;
    }

    const audio = this.getEventAudio(eventKey);
    return this.triggerAudioPlayback(audio);
  };

  playTestSound = eventKey => {
    const audio = this.getEventAudio(eventKey);
    return this.triggerAudioPlayback(audio);
  };

  shouldRepeatNewMessageSound = () => {
    return (
      this.notificationSoundConfig.repeatNewMessageSoundUntilRead &&
      this.isNotificationSoundEnabled(NOTIFICATION_SOUND_EVENTS.NEW_MESSAGE)
    );
  };

  set = ({
    currentUser,
    alwaysPlayAudioAlert,
    alertIfUnreadConversationExist,
    audioAlertType = DEFAULT_ALERT_TYPE,
    audioAlertTone = DEFAULT_TONE,
    notificationSoundEnabled = DEFAULT_NOTIFICATION_SOUND_ENABLED,
    repeatNewMessageSoundUntilRead = DEFAULT_REPEAT_NEW_MESSAGE_SOUND_UNTIL_READ,
    notificationSoundPreferences = DEFAULT_NOTIFICATION_SOUND_PREFERENCES,
  }) => {
    const serializedAudioAlertType =
      typeof audioAlertType === 'string'
        ? audioAlertType
        : DEFAULT_ALERT_TYPE_STRING;
    const resolvedTone = this.resolveLegacyTone(audioAlertTone || DEFAULT_TONE);

    this.notificationConfig = {
      ...this.notificationConfig,
      audioAlertType: serializedAudioAlertType.split('+').filter(Boolean),
      playAlertOnlyWhenHidden: !alwaysPlayAudioAlert,
      alertIfUnreadConversationExist: alertIfUnreadConversationExist,
    };

    this.notificationSoundConfig = {
      ...this.notificationSoundConfig,
      enabled: notificationSoundEnabled,
      repeatNewMessageSoundUntilRead: repeatNewMessageSoundUntilRead,
      preferences: sanitizeNotificationSoundPreferences(
        notificationSoundPreferences
      ),
    };

    this.currentUser = currentUser;

    const previousAudioTone = this.audioConfig.tone;
    this.audioConfig = {
      ...this.audioConfig,
      tone: resolvedTone,
    };

    // Always initialize audio if not exists, or if tone changed
    if (previousAudioTone !== resolvedTone || !this.audioConfig.audio) {
      this.intializeAudio();
    }

    initFaviconSwitcher();
    this.clearRecurringTimer();
    this.playAudioEvery30Seconds();
  };

  shouldPlayAlert = () => {
    if (this.notificationConfig.playAlertOnlyWhenHidden) {
      return !WindowVisibilityHelper.isWindowVisible();
    }
    return true;
  };

  shouldPlayNotificationEvents = () => {
    if (this.notificationSoundConfig.enabled) {
      return true;
    }

    return this.shouldPlayAlert();
  };

  executeRecurringNotification = () => {
    const shouldRepeatWithNotificationSound =
      this.shouldRepeatNewMessageSound();
    const canPlayNow = shouldRepeatWithNotificationSound
      ? true
      : this.shouldPlayAlert();

    if (this.store.hasUnreadConversation() && canPlayNow) {
      if (shouldRepeatWithNotificationSound) {
        this.playNotificationSound(NOTIFICATION_SOUND_EVENTS.NEW_MESSAGE);
      } else {
        this.playAudioAlert();
      }
      showBadgeOnFavicon();
    }
    this.resetRecurringTimer();
  };

  clearRecurringTimer = () => {
    if (this.recurringNotificationTimer) {
      clearTimeout(this.recurringNotificationTimer);
    }
  };

  resetRecurringTimer = () => {
    this.clearRecurringTimer();
    this.recurringNotificationTimer = setTimeout(
      this.executeRecurringNotification,
      NOTIFICATION_TIME
    );
  };

  playAudioEvery30Seconds = () => {
    const { audioAlertType, alertIfUnreadConversationExist } =
      this.notificationConfig;
    const hasLegacyRepeatEnabled =
      !audioAlertType.includes('none') && alertIfUnreadConversationExist;

    // No repeat mode is enabled, dismiss the timer.
    if (!hasLegacyRepeatEnabled && !this.shouldRepeatNewMessageSound()) return;

    this.resetRecurringTimer();
  };

  shouldNotifyOnMessage = message => {
    const { audioAlertType } = this.notificationConfig;
    if (audioAlertType.includes('none')) return false;
    if (audioAlertType.includes('all')) return true;

    const assignedToMe = isConversationAssignedToMe(
      message,
      this.currentUser.id
    );
    const isUnassigned = isConversationUnassigned(message);

    const shouldPlayAudio = [];

    if (
      audioAlertType.includes(EVENT_TYPES.ASSIGNED) ||
      audioAlertType.includes('mine')
    ) {
      shouldPlayAudio.push(assignedToMe);
    }
    if (audioAlertType.includes(EVENT_TYPES.UNASSIGNED)) {
      shouldPlayAudio.push(isUnassigned);
    }
    if (audioAlertType.includes(EVENT_TYPES.NOTME)) {
      shouldPlayAudio.push(!isUnassigned && !assignedToMe);
    }

    return shouldPlayAudio.some(Boolean);
  };

  onNewMessage = message => {
    // If currentUser is not initialized, dismiss the alert
    if (!this.currentUser) {
      return;
    }

    // If the user does not have the permission to view the conversation, then dismiss the alert
    // FIX ME: There shouldn't be a new message if the user has no access to the conversation.
    if (!this.store.hasConversationPermission(this.currentUser)) {
      return;
    }

    // If the conversation status is pending, then dismiss the alert
    // This case is common for all audio event types
    if (this.store.isMessageFromPendingConversation(message)) {
      return;
    }

    // If the message is sent by the current user then dismiss the alert
    if (isMessageFromCurrentUser(message, this.currentUser.id)) {
      return;
    }

    const shouldUseNotificationSound = this.isNotificationSoundEnabled(
      NOTIFICATION_SOUND_EVENTS.NEW_MESSAGE
    );
    if (!shouldUseNotificationSound && !this.shouldNotifyOnMessage(message)) {
      return;
    }

    // If the message type is not incoming or private, then dismiss the alert
    const { message_type: messageType, private: isPrivate } = message;
    if (messageType !== MESSAGE_TYPE.INCOMING && !isPrivate) {
      return;
    }

    if (WindowVisibilityHelper.isWindowVisible()) {
      // If the user looking at the conversation, then dismiss the alert
      if (this.store.isMessageFromCurrentConversation(message)) {
        return;
      }

      // If the user has disabled alerts when active on the dashboard, the dismiss the alert
      if (
        this.notificationConfig.playAlertOnlyWhenHidden &&
        !shouldUseNotificationSound
      ) {
        return;
      }
    }

    const hasPlayedNotificationSound = this.playNotificationSound(
      NOTIFICATION_SOUND_EVENTS.NEW_MESSAGE
    );

    if (!hasPlayedNotificationSound) {
      this.playAudioAlert();
    }

    showBadgeOnFavicon();
    this.playAudioEvery30Seconds();
  };

  onConversationCreated = conversation => {
    if (
      !this.currentUser ||
      !this.store.hasConversationPermission(this.currentUser)
    ) {
      return;
    }

    const performerId = conversation?.performer?.id;
    if (performerId && Number(performerId) === Number(this.currentUser.id)) {
      return;
    }

    if (!this.shouldPlayNotificationEvents()) {
      return;
    }

    const contactId = conversation?.meta?.sender?.id;
    const hasPreviousConversation =
      this.store.hasPreviousConversationForContact(contactId, conversation?.id);
    const visitorEventKey = hasPreviousConversation
      ? NOTIFICATION_SOUND_EVENTS.RETURNING_VISITORS
      : NOTIFICATION_SOUND_EVENTS.NEW_VISITORS;

    const hasPlayedVisitorSound = this.playNotificationSound(visitorEventKey);
    const hasPlayedIncomingChatSound = hasPlayedVisitorSound
      ? false
      : this.playNotificationSound(NOTIFICATION_SOUND_EVENTS.INCOMING_CHATS);

    if (hasPlayedVisitorSound || hasPlayedIncomingChatSound) {
      showBadgeOnFavicon();
    }
  };

  onConversationStatusChange = conversation => {
    if (
      !this.currentUser ||
      !this.store.hasConversationPermission(this.currentUser)
    ) {
      return;
    }

    if (!this.shouldPlayNotificationEvents()) {
      return;
    }

    if (conversation?.status !== 'pending') {
      return;
    }

    if (
      this.playNotificationSound(NOTIFICATION_SOUND_EVENTS.VISITORS_IN_QUEUE)
    ) {
      showBadgeOnFavicon();
    }
  };

  onNotificationCreated = notification => {
    if (
      !this.currentUser ||
      !this.store.hasConversationPermission(this.currentUser)
    ) {
      return;
    }

    if (!this.shouldPlayNotificationEvents()) {
      return;
    }

    const soundEventKey =
      NOTIFICATION_TYPE_TO_SOUND_EVENT[notification?.notification_type];
    if (!soundEventKey) {
      return;
    }

    if (this.playNotificationSound(soundEventKey)) {
      showBadgeOnFavicon();
    }
  };
}

export default new DashboardAudioNotificationHelper(GlobalStore);
